import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LayoutTemplate, Plus, Edit, Trash, RefreshCw, Copy, ArrowUp, ArrowDown, X, Lock, Users, Save, Eye } from 'lucide-react';
import { toast } from 'react-toastify';
import { canonicalPayrollCode, getPayrollDateRange } from '../payroll/core/domain';
import { VARIABLE_SOURCE_LABELS, validateTemplateInput } from '../payroll/core/configRules';
import { getPayrollVariableSource } from '../payroll/core/sourceRegistry';
import { ALL_PRODUCTIVITY_TEAMS } from '../payroll/core/templateResolver';
import {
    assembleTemplates, buildTeamsByEmployee, isEmployeeInPeriod, resolveAgreementsAt, resolvePeriodEmployees,
} from '../payroll/core/periodInputs';
import { deleteTemplate, loadTemplateData, saveTemplate } from '../payroll/data/configActions';
import { PAYROLL_PATHS, canManagePayroll, canViewPayrollPage } from '../payroll/data/permissions';
import {
    AccessDenied, Badge, ConfirmDialog, EmptyState, Field, FilterField, IconAction, ListToolbar, MessageList, Modal, PageShell, Pagination,
    SearchInput, SearchSelect, SectionTitle, SortableTh, Spinner, StatCards, buttonPrimary, buttonSecondary, employeeOptions, inputClass,
    matchesSearch, tableClass, tbodyClass, tdClass, thClass, theadClass, usePagination, useSortedRows,
} from '../payroll/components/ui';

const SOURCE_TONES = { NHAP_KY: 'yellow', THOA_THUAN: 'purple', CONG_THUC: 'green', NANG_SUAT: 'orange', UNG_LUONG: 'red', HE_THONG: 'blue' };

function columnSource(column, variable) {
    if (String(column.CONG_THUC ?? '').trim() || variable?.CONG_THUC) return 'CONG_THUC';
    return getPayrollVariableSource(column.BIEN_ID, variable?.NGUON_DU_LIEU);
}

const ALL_PRODUCTIVITY_LABEL = 'Mọi người nhận năng suất';

function scopeOf(template) {
    if (template.NHAN_SU_LIST?.length) return 'NHAN_SU';
    if (template.TO_LIST?.length) return 'TO';
    return 'TOAN_CONG_TY';
}

function scopeSummary(template) {
    const parts = [];
    const teams = (template.TO_LIST ?? []).filter((team) => team !== ALL_PRODUCTIVITY_TEAMS);
    if (template.TO_LIST?.includes(ALL_PRODUCTIVITY_TEAMS)) parts.push(ALL_PRODUCTIVITY_LABEL);
    if (teams.length) parts.push(`Tổ: ${teams.join(', ')}`);
    if (template.NHAN_SU_LIST?.length) parts.push(`${template.NHAN_SU_LIST.length} nhân sự`);
    if (!parts.length) parts.push('Toàn công ty');
    if (template.NHAN_SU_LOAI_TRU?.length) parts.push(`trừ ${template.NHAN_SU_LOAI_TRU.length} người`);
    return parts.join(' · ');
}

// Cột của mẫu đã lưu thành dạng form đang soạn.
function openColumns(template) {
    return (template.CHI_TIETS ?? []).map((column) => ({
        BIEN_ID: canonicalPayrollCode(column.BIEN_ID), CONG_THUC: column.CONG_THUC || '', BAT_BUOC: column.BAT_BUOC, HIEN_THI: column.HIEN_THI !== false, CHO_PHEP_NHAP: column.CHO_PHEP_NHAP,
    }));
}

function TemplateEditor({ initial, isNew, data, onClose, onSaved, onCopy }) {
    const readOnly = !canManagePayroll() || (!isNew && initial.template.KHOA_THAY_DOI);
    const [template, setTemplate] = useState(initial.template);
    const [columns, setColumns] = useState(initial.columns);
    const [newColumn, setNewColumn] = useState('');
    const [saving, setSaving] = useState(false);
    const variableByCode = useMemo(() => new Map(data.variables.map((variable) => [variable.MA_BIEN, variable])), [data.variables]);
    const activeVariables = data.variables.filter((variable) => variable.HOAT_DONG);
    const set = (field, value) => setTemplate((prev) => ({ ...prev, [field]: value }));
    const updateColumn = (index, patch) => setColumns((prev) => prev.map((column, i) => (i === index ? { ...column, ...patch } : column)));
    const moveColumn = (index, delta) => setColumns((prev) => {
        const next = [...prev];
        const target = index + delta;
        if (target < 0 || target >= next.length) return prev;
        [next[index], next[target]] = [next[target], next[index]];
        return next;
    });

    const validation = useMemo(() => validateTemplateInput(template, columns, {
        variables: data.variables,
        existingCodes: data.templates.map((item) => item.MA_MAU),
        isNew,
    }), [template, columns, data, isNew]);

    // Tổ chỉ lấy từ Phân bổ nhân sự: đó là nơi duy nhất quyết định ai thuộc tổ nào.
    const teamOptions = useMemo(() => {
        const teams = new Set(data.allocations.map((allocation) => allocation.TO).filter(Boolean));
        return [
            { value: ALL_PRODUCTIVITY_TEAMS, label: ALL_PRODUCTIVITY_LABEL, sublabel: 'Ai phụ trách công đoạn trong Phân bổ' },
            ...Array.from(teams).sort((left, right) => left.localeCompare(right, 'vi', { numeric: true })).map((team) => ({ value: team, label: team })),
        ];
    }, [data]);
    const people = useMemo(() => employeeOptions(data.employees), [data.employees]);
    const usedCodes = new Set(columns.map((column) => column.BIEN_ID));
    const nameOf = (code) => variableByCode.get(code)?.TEN_BIEN || code;

    const handleAddColumn = () => {
        if (!newColumn) return;
        const variable = variableByCode.get(newColumn);
        setColumns((prev) => [...prev, {
            BIEN_ID: newColumn, CONG_THUC: '', BAT_BUOC: false, HIEN_THI: true,
            CHO_PHEP_NHAP: getPayrollVariableSource(newColumn, variable?.NGUON_DU_LIEU) === 'NHAP_KY' && !variable?.CONG_THUC,
        }]);
        setNewColumn('');
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await saveTemplate({ ...template, MA_MAU: validation.code }, columns, { isNew, previousColumns: initial.previousColumns });
            toast.success(isNew ? 'Đã thêm mẫu bảng lương' : 'Đã cập nhật mẫu bảng lương');
            onSaved();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const { compiled } = validation;
    return (
        <Modal title={isNew ? 'Thêm mẫu bảng lương' : `${readOnly ? 'Xem' : 'Cập nhật'} mẫu ${initial.template.MA_MAU}`}
            subtitle="Bộ cột của bảng lương và phạm vi nhân sự áp dụng" size="max-w-6xl" onClose={onClose} disabled={saving}
            footer={(
                <>
                    {!isNew && <button className={buttonSecondary} onClick={() => onCopy(template, columns)} disabled={saving}><Copy className="w-4 h-4" />Nhân bản thành mẫu mới</button>}
                    <button className={buttonSecondary} onClick={onClose} disabled={saving}>{readOnly ? 'Đóng' : 'Hủy'}</button>
                    {!readOnly && (
                        <button className={buttonPrimary} onClick={handleSave} disabled={saving || validation.errors.length > 0}>
                            <Save className="w-4 h-4" />{saving ? 'Đang lưu...' : 'Lưu mẫu'}
                        </button>
                    )}
                </>
            )}>
            {!isNew && initial.template.KHOA_THAY_DOI && (
                <div className="mb-4"><MessageList tone="yellow" items={['Mẫu đã được dùng trong kỳ lương đã duyệt nên bị khóa. Muốn thay đổi, hãy nhân bản thành mẫu mới.']} /></div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div className="lg:col-span-2 space-y-4">
                    <SectionTitle>Thông tin mẫu</SectionTitle>
                    <Field label="Mã mẫu" required>
                        <input className={inputClass} value={template.MA_MAU} disabled={!isNew || readOnly} onChange={(e) => set('MA_MAU', e.target.value.toUpperCase())} />
                    </Field>
                    <Field label="Tên mẫu" required>
                        <input className={inputClass} value={template.TEN_MAU} disabled={readOnly} onChange={(e) => set('TEN_MAU', e.target.value)} />
                    </Field>
                    <Field label="Mô tả">
                        <input className={inputClass} value={template.MO_TA} disabled={readOnly} onChange={(e) => set('MO_TA', e.target.value)} />
                    </Field>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" checked={template.HOAT_DONG !== false} disabled={readOnly} onChange={(e) => set('HOAT_DONG', e.target.checked)} />Đang hoạt động
                    </label>
                    <SectionTitle>Phạm vi áp dụng</SectionTitle>
                    <p className="text-xs text-gray-500">
                        Tổ của một người lấy theo Phân bổ nhân sự (công đoạn người đó phụ trách). Bỏ trống cả tổ và nhân sự là áp dụng toàn công ty. Một người khớp nhiều mẫu thì ưu tiên: thỏa thuận lương → mẫu nêu tên người đó → mẫu theo tổ → mẫu “{ALL_PRODUCTIVITY_LABEL}” → mẫu toàn công ty.
                    </p>
                    <Field label="Các tổ áp dụng">
                        <SearchSelect multiple options={teamOptions} value={template.TO_LIST} onChange={(value) => set('TO_LIST', value)} disabled={readOnly} placeholder="Tìm tổ..." maxHeight="max-h-36" />
                    </Field>
                    <Field label="Nhân sự áp dụng riêng">
                        <SearchSelect multiple options={people} value={template.NHAN_SU_LIST} onChange={(value) => set('NHAN_SU_LIST', value)} disabled={readOnly} placeholder="Tìm nhân sự..." maxHeight="max-h-36" />
                    </Field>
                    <Field label="Nhân sự loại trừ" hint="Người thuộc tổ ở trên nhưng không dùng mẫu này">
                        <SearchSelect multiple options={people} value={template.NHAN_SU_LOAI_TRU} onChange={(value) => set('NHAN_SU_LOAI_TRU', value)} disabled={readOnly} placeholder="Tìm nhân sự..." maxHeight="max-h-36" />
                    </Field>
                </div>
                <div className="lg:col-span-3 space-y-4">
                    <SectionTitle>Các cột của bảng lương ({columns.length})</SectionTitle>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="max-h-[50vh] overflow-y-auto divide-y divide-gray-200">
                            {columns.map((column, index) => {
                                const variable = variableByCode.get(column.BIEN_ID);
                                const source = columnSource(column, variable);
                                return (
                                    <div key={column.BIEN_ID} className="px-3 py-2.5 hover:bg-gray-50">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-gray-400 w-5">{index + 1}</span>
                                            <span className="font-mono text-sm font-medium text-indigo-600 w-20">{column.BIEN_ID}</span>
                                            <span className="text-sm text-gray-800 flex-1 truncate" title={nameOf(column.BIEN_ID)}>{nameOf(column.BIEN_ID)}</span>
                                            <Badge tone={SOURCE_TONES[source] || 'blue'}>{VARIABLE_SOURCE_LABELS[source] || source}</Badge>
                                            {!readOnly && (
                                                <div className="flex">
                                                    <IconAction icon={ArrowUp} title="Lên" tone="gray" onClick={() => moveColumn(index, -1)} disabled={index === 0} />
                                                    <IconAction icon={ArrowDown} title="Xuống" tone="gray" onClick={() => moveColumn(index, 1)} disabled={index === columns.length - 1} />
                                                    <IconAction icon={X} title="Bỏ cột" tone="red" onClick={() => setColumns((prev) => prev.filter((_, i) => i !== index))} />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-3 mt-2 ml-7">
                                            <input className={`${inputClass} font-mono text-xs flex-1 min-w-[200px]`} value={column.CONG_THUC} disabled={readOnly}
                                                placeholder={variable?.CONG_THUC ? `Mặc định: ${variable.CONG_THUC}` : 'Công thức riêng của mẫu (không bắt buộc)'}
                                                onChange={(e) => updateColumn(index, { CONG_THUC: e.target.value.toUpperCase() })} />
                                            <label className="flex items-center gap-1 text-xs text-gray-600"><input type="checkbox" checked={column.BAT_BUOC} disabled={readOnly} onChange={(e) => updateColumn(index, { BAT_BUOC: e.target.checked })} />Bắt buộc</label>
                                            <label className="flex items-center gap-1 text-xs text-gray-600"><input type="checkbox" checked={column.HIEN_THI} disabled={readOnly} onChange={(e) => updateColumn(index, { HIEN_THI: e.target.checked })} />Hiển thị</label>
                                            <label className="flex items-center gap-1 text-xs text-gray-600" title="Chỉ biến nguồn Nhập theo kỳ mới nhập được trên bảng lương">
                                                <input type="checkbox" checked={column.CHO_PHEP_NHAP} disabled={readOnly || source !== 'NHAP_KY'} onChange={(e) => updateColumn(index, { CHO_PHEP_NHAP: e.target.checked })} />Cho nhập
                                            </label>
                                        </div>
                                    </div>
                                );
                            })}
                            {columns.length === 0 && <div className="text-center py-6 text-sm text-gray-400">Chưa có cột nào</div>}
                        </div>
                        {!readOnly && (
                            <div className="flex gap-2 p-3 border-t border-gray-200 bg-gray-50">
                                <select className={inputClass} value={newColumn} onChange={(e) => setNewColumn(e.target.value)}>
                                    <option value="">— Chọn biến để thêm cột —</option>
                                    {activeVariables.filter((variable) => !usedCodes.has(variable.MA_BIEN)).map((variable) => (
                                        <option key={variable.MA_BIEN} value={variable.MA_BIEN}>{variable.MA_BIEN} — {variable.TEN_BIEN}</option>
                                    ))}
                                </select>
                                <button className={buttonSecondary} onClick={handleAddColumn} disabled={!newColumn}><Plus className="w-4 h-4" />Thêm</button>
                            </div>
                        )}
                    </div>
                    <MessageList items={validation.errors} />
                    <MessageList tone="yellow" items={validation.warnings} />
                    {compiled && !validation.errors.length && (
                        <MessageList tone="blue" items={[
                            `Cần nhập trong thỏa thuận lương: ${compiled.agreementCodes.join(', ') || 'không có'}`,
                            `Nhập trên bảng lương mỗi kỳ: ${compiled.inputCodes.join(', ') || 'không có'}`,
                            ...(compiled.dependencyCodes.length ? [`Biến tính ngầm (không hiện cột): ${compiled.dependencyCodes.join(', ')}`] : []),
                        ]} />
                    )}
                </div>
            </div>
        </Modal>
    );
}

function TemplateDetail({ template, variables, usage, onClose }) {
    const variableByCode = new Map(variables.map((variable) => [variable.MA_BIEN, variable]));
    return (
        <Modal title={template.TEN_MAU} subtitle={`Mã ${template.MA_MAU} · Phiên bản ${template.PHIEN_BAN || 1}`} size="max-w-4xl" onClose={onClose}
            footer={<button className={buttonSecondary} onClick={onClose}>Đóng</button>}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-5">
                <div><p className="text-gray-500">Phạm vi</p><p className="font-medium text-gray-900">{scopeSummary(template)}</p></div>
                <div><p className="text-gray-500">Đang áp dụng</p><p className="font-medium text-gray-900">{usage} nhân sự</p></div>
                <div><p className="text-gray-500">Trạng thái</p><Badge tone={template.HOAT_DONG ? 'green' : 'gray'}>{template.HOAT_DONG ? 'Hoạt động' : 'Ngưng dùng'}</Badge></div>
                <div><p className="text-gray-500">Khóa</p>{template.KHOA_THAY_DOI ? <Badge tone="yellow">Đã khóa</Badge> : <span className="text-gray-700">Chưa khóa</span>}</div>
            </div>
            {template.MO_TA && <p className="text-sm text-gray-600 mb-4">{template.MO_TA}</p>}
            <SectionTitle>Danh sách cột biến số</SectionTitle>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className={tableClass}>
                    <thead className={theadClass}>
                        <tr><th className={thClass}>#</th><th className={thClass}>Mã</th><th className={thClass}>Tên cột</th><th className={thClass}>Nguồn</th><th className={thClass}>Công thức riêng</th><th className={thClass}>Thuộc tính</th></tr>
                    </thead>
                    <tbody className={tbodyClass}>
                        {template.CHI_TIETS.map((column, index) => {
                            const code = canonicalPayrollCode(column.BIEN_ID);
                            const source = columnSource(column, variableByCode.get(code));
                            return (
                                <tr key={column.ID || code}>
                                    <td className={tdClass}>{index + 1}</td>
                                    <td className={`${tdClass} font-mono font-medium text-indigo-600`}>{code}</td>
                                    <td className={tdClass}>{variableByCode.get(code)?.TEN_BIEN || code}</td>
                                    <td className={tdClass}><Badge tone={SOURCE_TONES[source] || 'blue'}>{VARIABLE_SOURCE_LABELS[source] || source}</Badge></td>
                                    <td className={`${tdClass} font-mono text-xs`}>{column.CONG_THUC || '—'}</td>
                                    <td className={`${tdClass} text-xs space-x-1`}>
                                        {column.BAT_BUOC && <Badge tone="red">Bắt buộc</Badge>}
                                        {column.CHO_PHEP_NHAP && <Badge tone="yellow">Cho nhập</Badge>}
                                        {column.HIEN_THI === false && <Badge>Ẩn</Badge>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </Modal>
    );
}

const COVERAGE_TONE = { RESOLVED: 'green', CONFLICT: 'red', EXCLUDED: 'yellow' };
const COVERAGE_LABEL = { RESOLVED: 'Có mẫu', CONFLICT: 'Xung đột', EXCLUDED: 'Không có mẫu' };

function CoverageModal({ coverage, onClose }) {
    const [filter, setFilter] = useState('ALL');
    const [search, setSearch] = useState('');
    const rows = [
        ...coverage.employees.map((item) => ({ ...item, status: 'RESOLVED', detail: `${item.templateName} — ${item.templateReason}` })),
        ...coverage.conflicts.map((item) => ({ ...item, status: 'CONFLICT', detail: `Khớp cùng lúc ${item.templates.map((template) => template.templateName).join(', ')}; thu hẹp phạm vi để mỗi người chỉ thuộc một mẫu.` })),
        ...coverage.excludedEmployees.map((item) => ({ ...item, status: 'EXCLUDED', detail: item.reason })),
    ].filter((row) => (filter === 'ALL' || row.status === filter) && matchesSearch(`${row.employeeName} ${row.employeeCode} ${row.team}`, search))
        .sort((left, right) => String(left.employeeName).localeCompare(String(right.employeeName), 'vi'));
    return (
        <Modal title="Độ phủ nhân sự" subtitle="Nhân sự đang làm trong tháng này dùng mẫu nào; mỗi người phải khớp đúng một mẫu mới vào được bảng lương" icon={Users} size="max-w-5xl" onClose={onClose}
            footer={<button className={buttonSecondary} onClick={onClose}>Đóng</button>}>
            <StatCards active={filter} onSelect={setFilter} cards={[
                { key: 'all', label: 'Tất cả', value: coverage.employees.length + coverage.conflicts.length + coverage.excludedEmployees.length, tone: 'indigo', filterValue: 'ALL' },
                { key: 'ok', label: 'Có mẫu', value: coverage.employees.length, tone: 'green', filterValue: 'RESOLVED' },
                { key: 'conflict', label: 'Xung đột', value: coverage.conflicts.length, tone: 'red', filterValue: 'CONFLICT' },
                { key: 'excluded', label: 'Không có mẫu', value: coverage.excludedEmployees.length, tone: 'yellow', filterValue: 'EXCLUDED' },
            ]} />
            <div className="mb-4"><SearchInput value={search} onChange={setSearch} placeholder="Tìm nhân sự, Số HĐ, tổ..." /></div>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className={tableClass}>
                    <thead className={theadClass}><tr><th className={thClass}>Nhân sự</th><th className={thClass}>Tổ</th><th className={thClass}>Kết quả</th><th className={thClass}>Chi tiết</th></tr></thead>
                    <tbody className={tbodyClass}>
                        {rows.map((row) => (
                            <tr key={row.employeeId} className="hover:bg-gray-50">
                                <td className={tdClass}><div className="font-medium text-gray-900">{row.employeeName}</div><div className="text-xs text-gray-500">{row.employeeCode}</div></td>
                                <td className={tdClass}>{row.team || '—'}</td>
                                <td className={tdClass}><Badge tone={COVERAGE_TONE[row.status]}>{COVERAGE_LABEL[row.status]}</Badge></td>
                                <td className={`${tdClass} text-xs`}>{row.detail}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-sm text-gray-400">Không có nhân sự nào</td></tr>}
                    </tbody>
                </table>
            </div>
        </Modal>
    );
}

const EMPTY_TEMPLATE = { MA_MAU: '', TEN_MAU: '', MO_TA: '', HOAT_DONG: true, TO_LIST: [], NHAN_SU_LIST: [], NHAN_SU_LOAI_TRU: [], PHIEN_BAN: 1, KHOA_THAY_DOI: false };

const MauBangLuong = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('ALL');
    const [scope, setScope] = useState('ALL');
    const [editor, setEditor] = useState(null);
    const [detail, setDetail] = useState(null);
    const [showCoverage, setShowCoverage] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const canView = canViewPayrollPage(PAYROLL_PATHS.templates);
    const canManage = canManagePayroll();

    const reload = useCallback(async () => {
        try {
            setLoading(true);
            setData(await loadTemplateData());
        } catch (error) {
            console.error('Lỗi tải mẫu bảng lương:', error);
            toast.error('Không tải được mẫu bảng lương');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (canView) reload(); }, [canView, reload]);

    const templates = useMemo(() => (data ? assembleTemplates(data.templates, data.templateColumns) : []), [data]);
    const coverage = useMemo(() => {
        if (!data) return null;
        const today = new Date();
        const range = getPayrollDateRange(today.getMonth() + 1, today.getFullYear());
        const employees = data.employees.filter((employee) => employee.SO_HD && isEmployeeInPeriod(employee, range));
        return resolvePeriodEmployees({
            employees,
            teamsByEmployee: buildTeamsByEmployee(employees, data.allocations, range.to),
            agreementByEmployee: resolveAgreementsAt(data.agreements, range.to),
            selectedTemplates: templates.filter((template) => template.HOAT_DONG),
            allTemplates: templates,
        });
    }, [data, templates]);
    const usageByTemplate = useMemo(() => {
        const counts = new Map();
        for (const item of coverage?.employees ?? []) counts.set(item.templateId, (counts.get(item.templateId) ?? 0) + 1);
        return counts;
    }, [coverage]);

    const filtered = useMemo(() => templates.filter((template) => {
        if (filter === 'COMPANY' && scopeOf(template) !== 'TOAN_CONG_TY') return false;
        if (filter === 'LOCKED' && !template.KHOA_THAY_DOI) return false;
        if (filter === 'INACTIVE' && template.HOAT_DONG) return false;
        if (filter === 'ACTIVE' && !template.HOAT_DONG) return false;
        if (scope !== 'ALL' && scopeOf(template) !== scope) return false;
        return matchesSearch(`${template.MA_MAU} ${template.TEN_MAU}`, search);
    }), [templates, filter, scope, search]);
    const accessors = useMemo(() => ({
        code: (row) => row.MA_MAU, name: (row) => row.TEN_MAU, columns: (row) => row.CHI_TIETS.length, usage: (row) => usageByTemplate.get(row.MA_MAU) ?? 0,
    }), [usageByTemplate]);
    const { sorted, sort, toggleSort } = useSortedRows(filtered, accessors);
    const { pageRows, pagination } = usePagination(sorted);

    if (!canView) return <AccessDenied />;

    const openEditor = (template, isNew) => setEditor({
        isNew,
        initial: { template: { ...EMPTY_TEMPLATE, ...template }, columns: openColumns(template), previousColumns: isNew ? [] : template.CHI_TIETS ?? [] },
    });
    const handleCopy = (template, columns) => setEditor({
        isNew: true,
        initial: {
            template: { ...template, MA_MAU: `${template.MA_MAU}_2`, TEN_MAU: `${template.TEN_MAU} (bản sao)`, KHOA_THAY_DOI: false, PHIEN_BAN: 1 },
            columns: columns.map((column) => ({ ...column })),
            previousColumns: [],
        },
    });
    const problems = coverage ? coverage.conflicts.length + coverage.excludedEmployees.length : 0;

    const actions = (template) => (
        <div className="flex justify-center gap-1">
            <IconAction icon={Eye} title="Xem chi tiết" tone="blue" onClick={() => setDetail(template)} />
            {canManage && (
                <>
                    <IconAction icon={template.KHOA_THAY_DOI ? Lock : Edit} title={template.KHOA_THAY_DOI ? 'Mẫu đã khóa (xem)' : 'Sửa'} onClick={() => openEditor(template, false)} />
                    <IconAction icon={Copy} title="Nhân bản" tone="green" onClick={() => handleCopy(template, openColumns(template))} />
                    <IconAction icon={Trash} title="Xóa" tone="red" onClick={() => setDeleteTarget(template)} />
                </>
            )}
        </div>
    );

    return (
        <PageShell icon={LayoutTemplate} title="Mẫu bảng lương" subtitle="Bộ cột, công thức và phạm vi áp dụng của từng loại bảng lương"
            actions={(
                <>
                    <button className={buttonSecondary} onClick={reload} disabled={loading}><RefreshCw className="w-4 h-4" />Làm mới</button>
                    <button className={buttonSecondary} onClick={() => setShowCoverage(true)} disabled={!coverage}>
                        <Users className="w-4 h-4" />Độ phủ nhân sự
                        {problems > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1 text-xs rounded-full bg-red-600 text-white">{problems}</span>}
                    </button>
                    {canManage && <button className={buttonPrimary} onClick={() => openEditor(EMPTY_TEMPLATE, true)} disabled={!data}><Plus className="w-4 h-4" />Thêm mẫu</button>}
                </>
            )}>
            <StatCards active={filter} onSelect={setFilter} cards={[
                { key: 'total', label: 'Tổng mẫu', value: templates.length, tone: 'indigo', filterValue: 'ALL' },
                { key: 'company', label: 'Áp dụng toàn công ty', value: templates.filter((template) => scopeOf(template) === 'TOAN_CONG_TY').length, tone: 'blue', filterValue: 'COMPANY' },
                { key: 'locked', label: 'Đã khóa', value: templates.filter((template) => template.KHOA_THAY_DOI).length, tone: 'yellow', caption: 'Đã dùng ở kỳ đã duyệt', filterValue: 'LOCKED' },
                { key: 'inactive', label: 'Ngưng dùng', value: templates.filter((template) => !template.HOAT_DONG).length, tone: 'red', filterValue: 'INACTIVE' },
            ]} />
            {problems > 0 && (
                <div className="mb-4"><MessageList tone="yellow" items={[`${coverage.conflicts.length} nhân sự khớp nhiều mẫu cùng lúc và ${coverage.excludedEmployees.length} nhân sự chưa có mẫu. Bấm "Độ phủ nhân sự" để xem từng người.`]} /></div>
            )}
            <ListToolbar search={search} onSearch={setSearch} placeholder="Tìm mã hoặc tên mẫu..."
                filterCount={[filter, scope].filter((value) => value !== 'ALL').length}
                onClearFilters={() => { setFilter('ALL'); setScope('ALL'); setSearch(''); }}
                filters={(
                    <>
                        <FilterField label="Phạm vi">
                            <select className={inputClass} value={scope} onChange={(e) => setScope(e.target.value)}>
                                <option value="ALL">Tất cả phạm vi</option><option value="TOAN_CONG_TY">Toàn công ty</option><option value="TO">Theo tổ</option><option value="NHAN_SU">Theo nhân sự</option>
                            </select>
                        </FilterField>
                        <FilterField label="Trạng thái">
                            <select className={inputClass} value={filter} onChange={(e) => setFilter(e.target.value)}>
                                <option value="ALL">Tất cả trạng thái</option><option value="ACTIVE">Hoạt động</option><option value="INACTIVE">Ngưng dùng</option><option value="LOCKED">Đã khóa</option>
                            </select>
                        </FilterField>
                    </>
                )} />
            {loading || !data ? <Spinner /> : sorted.length === 0 ? (
                <EmptyState icon={LayoutTemplate} title="Chưa có mẫu bảng lương">Vào Cấu hình công lương để khởi tạo danh mục mặc định, hoặc bấm Thêm mẫu.</EmptyState>
            ) : (
                <>
                    <div className="space-y-3 md:hidden">
                        {pageRows.map((template) => (
                            <div key={template.MA_MAU} className="border border-gray-200 rounded-lg p-4 space-y-2">
                                <div className="flex justify-between items-start gap-2">
                                    <div><p className="font-medium text-gray-900">{template.TEN_MAU}</p><p className="text-xs text-gray-500 font-mono">{template.MA_MAU} · v{template.PHIEN_BAN || 1}</p></div>
                                    <Badge tone={template.HOAT_DONG ? 'green' : 'gray'}>{template.HOAT_DONG ? 'Hoạt động' : 'Ngưng dùng'}</Badge>
                                </div>
                                <p className="text-sm text-gray-700">{scopeSummary(template)}</p>
                                <p className="text-xs text-gray-500">{template.CHI_TIETS.length} cột · {usageByTemplate.get(template.MA_MAU) ?? 0} nhân sự đang dùng</p>
                                {actions(template)}
                            </div>
                        ))}
                    </div>
                    <div className="hidden md:block overflow-x-auto border border-gray-200 rounded-lg">
                        <table className={tableClass}>
                            <thead className={theadClass}>
                                <tr>
                                    <SortableTh label="Mã mẫu" sortKey="code" sort={sort} onSort={toggleSort} />
                                    <SortableTh label="Tên mẫu" sortKey="name" sort={sort} onSort={toggleSort} />
                                    <th className={thClass}>Phạm vi áp dụng</th>
                                    <SortableTh label="Số cột" sortKey="columns" sort={sort} onSort={toggleSort} align="center" />
                                    <SortableTh label="Đang áp dụng" sortKey="usage" sort={sort} onSort={toggleSort} align="center" />
                                    <th className={thClass}>Trạng thái</th>
                                    <th className={`${thClass} text-center`}>Thao tác</th>
                                </tr>
                            </thead>
                            <tbody className={tbodyClass}>
                                {pageRows.map((template) => (
                                    <tr key={template.MA_MAU} className="hover:bg-gray-50 transition-colors">
                                        <td className={`${tdClass} font-mono font-medium text-indigo-600 whitespace-nowrap`}>{template.MA_MAU}</td>
                                        <td className={tdClass}><div className="font-medium text-gray-900">{template.TEN_MAU}</div><div className="text-xs text-gray-500">Phiên bản {template.PHIEN_BAN || 1}</div></td>
                                        <td className={tdClass}>{scopeSummary(template)}</td>
                                        <td className={`${tdClass} text-center`}>{template.CHI_TIETS.length}</td>
                                        <td className={`${tdClass} text-center font-semibold text-gray-900`}>{usageByTemplate.get(template.MA_MAU) ?? 0} người</td>
                                        <td className={`${tdClass} space-x-1 whitespace-nowrap`}>
                                            <Badge tone={template.HOAT_DONG ? 'green' : 'gray'}>{template.HOAT_DONG ? 'Hoạt động' : 'Ngưng dùng'}</Badge>
                                            {template.KHOA_THAY_DOI && <Badge tone="yellow" title="Đã dùng ở kỳ được duyệt nên không sửa được; hãy nhân bản để tạo phiên bản mới"><Lock className="w-3 h-3 mr-1" />Đã khóa</Badge>}
                                        </td>
                                        <td className={tdClass}>{actions(template)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination pagination={pagination} unit="mẫu" />
                </>
            )}
            {editor && <TemplateEditor {...editor} data={data} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); reload(); }} onCopy={handleCopy} />}
            {detail && <TemplateDetail template={detail} variables={data.variables} usage={usageByTemplate.get(detail.MA_MAU) ?? 0} onClose={() => setDetail(null)} />}
            {showCoverage && coverage && <CoverageModal coverage={coverage} onClose={() => setShowCoverage(false)} />}
            {deleteTarget && (
                <ConfirmDialog title="Xóa mẫu bảng lương" confirmLabel="Xóa mẫu" onClose={() => setDeleteTarget(null)}
                    message={<>Xóa mẫu <b>{deleteTarget.TEN_MAU}</b> ({deleteTarget.MA_MAU})? Nếu mẫu đã có thỏa thuận hoặc kỳ lương dùng, hệ thống sẽ chuyển sang ngưng dùng thay vì xóa.</>}
                    onConfirm={async () => {
                        try {
                            toast.success(await deleteTemplate(deleteTarget, data));
                            setDeleteTarget(null);
                            reload();
                        } catch (error) {
                            toast.error(error.message);
                        }
                    }} />
            )}
        </PageShell>
    );
};

export default MauBangLuong;
